/* Products-per-row toggle (desktop).

   Writes the chosen count to #ProductGridContainer as data-kashira-cols, which
   CSS reads to re-width the cards. The attribute lives on the container rather
   than the grid because facets.js replaces the container's innerHTML on every
   filter and sort — an attribute on the <ul> would survive exactly until someone
   ticked a filter.

   The choice is remembered in localStorage. Someone who prefers three-up is
   telling us something about their screen, not about this one collection, so
   asking again on the next page would be forgetting on purpose.

   The re-layout is animated with FLIP — measure every card, change the column
   count, measure again, then play each card from where it was to where it now
   is. The alternative, transitioning width directly, animates a layout property
   and forces a reflow per frame on every card in the grid; transform and opacity
   are composited and stay off the main thread.

   Built on the Web Animations API rather than a motion library. The effect is
   the same one Motion One would produce — it is a wrapper over this API — and
   this project's third-party JavaScript already sits well over the budget in
   README, so a new dependency would need a decision it has not been given. */

(() => {
  const KEY = 'kashira:grid-cols';
  const group = document.querySelector('[data-kashira-gridview]');
  const container = document.getElementById('ProductGridContainer');
  if (!group || !container) return;

  const buttons = [...group.querySelectorAll('.kashira-gridview__btn')];
  const allowed = buttons.map((b) => b.dataset.cols);
  const fallback = group.dataset.default || '4';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* The animation this component last gave each card, so it can be cancelled
     before the next one starts. A filled WAAPI animation stays attached to the
     element until it is cancelled — toggling four times left 48 of them stacked
     on the grid, and their transforms compounded until a card was sitting at
     scale(2). Tracked in a WeakMap rather than cancelling everything from
     getAnimations(), which would also kill the theme's scroll-reveal. */
  const playing = new WeakMap();

  /* FLIP: First, Last, Invert, Play. */
  const relayout = (change) => {
    const cards = [...container.querySelectorAll('.product-grid > .grid__item')];

    /* Reduced motion, an empty grid, or a browser without animate(): just make
       the change. Someone who asked the OS for less movement gets the new
       layout instantly, not a slower version of the same thing. */
    if (reduced.matches || !cards.length || !cards[0].animate) {
      change();
      return;
    }

    /* First: where each card is *visually* right now. If an earlier toggle is
       still playing, its transform is part of that — which is what we want, so
       an interrupted change carries on from where the eye last saw the card. */
    const first = new Map(cards.map((c) => [c, c.getBoundingClientRect()]));

    /* Then clear those animations, before Last is measured. Cancelling here and
       not at animate() time is the whole point: a rect read while a transform is
       still applied is the transformed rect, so measuring Last first would
       compute the delta against a card that is mid-flight and the new animation
       would start from the wrong place. Nothing paints between here and the
       animate() calls below, so the cards never visibly snap. */
    cards.forEach((card) => playing.get(card)?.cancel());

    change();

    cards.forEach((card, i) => {
      const a = first.get(card);
      const b = card.getBoundingClientRect();
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      const sx = a.width / b.width;
      if (!dx && !dy && Math.abs(sx - 1) < 0.01) return;

      const anim = card.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${sx})`, transformOrigin: 'top left' },
          { transform: 'none', transformOrigin: 'top left' },
        ],
        {
          duration: 320,
          /* Brand ease-out: quick to leave, soft to settle. */
          easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
          /* A short stagger reads as the grid reflowing rather than every card
             moving as one block. Capped so a long grid does not finish late. */
          delay: Math.min(i * 12, 160),
          /* 'backwards', not 'both'. The card must hold its OLD position during
             the stagger delay, or it would jump to the new layout and then
             animate back from it. But the end state is plain `transform: none`,
             which is where the card naturally sits — persisting it with 'both'
             leaves a finished animation attached to every card, and they pile up
             one per toggle. */
          fill: 'backwards',
        }
      );
      playing.set(card, anim);
      /* Drop the reference once it has played out, so a card that is never
         animated again is not held by this map. */
      anim.finished.then(() => { if (playing.get(card) === anim) playing.delete(card); }).catch(() => {});
    });
  };

  const apply = (cols, persist, animate) => {
    if (!allowed.includes(cols)) cols = fallback;
    const set = () => {
      container.dataset.kashiraCols = cols;
    };
    if (animate) relayout(set);
    else set();
    buttons.forEach((b) => {
      const on = b.dataset.cols === cols;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (persist) {
      try {
        localStorage.setItem(KEY, cols);
      } catch (e) {
        /* Safari private mode throws on setItem. The toggle still works for
           this page; it just will not be remembered, which is the right thing
           to lose. */
      }
    }
  };

  let stored = null;
  try {
    stored = localStorage.getItem(KEY);
  } catch (e) {
    /* Storage unavailable — fall through to the section's own setting. */
  }
  apply(stored || fallback, false, false);

  group.addEventListener('click', (event) => {
    const btn = event.target.closest('.kashira-gridview__btn');
    if (btn) apply(btn.dataset.cols, true, true);
  });
})();
